import { createAction } from '@reduxjs/toolkit';
import { Dispatch, AnyAction } from 'redux';

import { GetState } from 'store/types';
import { Action } from 'modules/types/common';
import { RegisterData, RegisterMeta, RegisterAlert, UnifiedRedirectKey } from './types';
import {
  registerDataAsConsumerSelector,
  registerAccountSelector,
  registerValidateConsumerHealthIdsSelector
} from './selectors';

export const CREATE_ACCOUNT = 'REGISTER.CREATE_ACCOUNT';
export const CREATE_ACCOUNT_SUCCESS = 'REGISTER.CREATE_ACCOUNT_SUCCESS';
export const CREATE_ACCOUNT_FAIL = 'REGISTER.CREATE_ACCOUNT_FAIL';
export const submitRegistration = () => (
  dispatch: Dispatch<AnyAction>,
  getState: GetState
): Promise<AnyAction> => {
  const state = getState();
  const consumer = registerDataAsConsumerSelector(state);
  const account = registerAccountSelector(state);

  const isSelectHealth = !!consumer.shSubscriberNumber;
  const idAvailable = !!(
    consumer.shSubscriberNumber ||
    consumer.pharmacyPlanId ||
    consumer.medicaidId
  );

  if (consumer.emails) {
    consumer.emails[0].verified = true;
  }

  if (consumer.phones) {
    consumer.phones[0].verified = true;
  }

  consumer.registrationStatus = isSelectHealth ? 'SelectHealthRegistration' : 'AccountInformation';

  return dispatch({
    type: CREATE_ACCOUNT,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/register/registerConsumer',
        data: {
          password: account.password,
          idAvailable,
          consumer: {
            ...consumer,
            userName: account.username,
            termAcceptance: account.termsAcceptance
          },
          consumerVerification: {
            sendOTP: false,
            verifyUser: false,
            verificationStatus: 'VerificationComplete',
            otpType: 'email',
            validateOTP: false,
            pin: null,
            emailValidationComplete: true,
            smsValidationComplete: true,
            verificationComplete: true
          }
        }
      }
    }
  });
};

export const UPDATE_STEP = 'REGISTER.UPDATE_STEP';
export const updateRegistrationStep = (payload: string) => ({
  type: UPDATE_STEP,
  payload
});
export const UPDATE_ACCOUNT_CREATION_INFO = 'REGISTER.UPDATE_ACCOUNT_CREATION_INFO';
export const updateAccountCreationInfo = (payload: boolean) => ({
  type: UPDATE_ACCOUNT_CREATION_INFO,
  payload
});

export const UPDATE_ALERT = 'REGISTER.UPDATE_ALERT';
export const updateRegistrationAlert = (payload: RegisterAlert) => ({
  type: UPDATE_ALERT,
  payload
});

export const UPDATE_DATA = 'REGISTER.UPDATE_DATA';
export const updateRegistrationData = (payload: Partial<RegisterData>) => ({
  type: UPDATE_DATA,
  payload
});

export const UPDATE_META = 'REGISTER.UPDATE_META';
export const updateRegistrationMeta = (payload: Partial<RegisterMeta>) => ({
  type: UPDATE_META,
  payload
});

export const UPDATE_REGISTRATION_SESSION = 'REGISTER.UPDATE_REGISTRATION_SESSION';
export const updateRegistrationSession = (payload: boolean) => ({
  type: UPDATE_REGISTRATION_SESSION,
  payload
});

export const VALIDATE_CONSUMER_HEALTH_IDS = 'REGISTER.VALIDATE_CONSUMER_HEALTH_IDS';
export const VALIDATE_CONSUMER_HEALTH_IDS_SUCCESS = 'REGISTER.VALIDATE_CONSUMER_HEALTH_IDS_SUCCESS';
export const VALIDATE_CONSUMER_HEALTH_IDS_FAIL = 'REGISTER.VALIDATE_CONSUMER_HEALTH_IDS_FAIL';
export const validateConsumerHealthIds = () => (
  dispatch: Dispatch<AnyAction>,
  getState: GetState
) => {
  const consumer = registerValidateConsumerHealthIdsSelector(getState());
  return dispatch({
    type: VALIDATE_CONSUMER_HEALTH_IDS,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/register/validateConsumerHealthIds',
        data: consumer
      }
    }
  });
};

export const VALIDATE_CONSUMER_ID = 'REGISTER.VALIDATE_CONSUMER_ID';
export const VALIDATE_CONSUMER_ID_SUCCESS = 'REGISTER.VALIDATE_CONSUMER_ID_SUCCESS';
export const VALIDATE_CONSUMER_ID_FAIL = 'REGISTER.VALIDATE_CONSUMER_ID_FAIL';
export const validateConsumerId = (payload: string) => (dispatch: Dispatch<AnyAction>) => {
  return dispatch({
    type: VALIDATE_CONSUMER_ID,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/register/validateConsumerId',
        data: {
          consumerId: payload
        }
      }
    }
  });
};

export const SEND_VERIFICATION_CODE = 'REGISTER.REQUEST_VERIFICATION_CODE';
export const SEND_VERIFICATION_CODE_SUCCESS = 'REGISTER.REQUEST_VERIFICATION_CODE_SUCCESS';
export const SEND_VERIFICATION_CODE_FAIL = 'REGISTER.REQUEST_VERIFICATION_CODE_FAIL';
export const sendVerficationCode = (otpType: 'email' | 'sms') => (
  dispatch: Dispatch<AnyAction>,
  getState: GetState
) => {
  const consumer = registerDataAsConsumerSelector(getState());

  return dispatch({
    type: SEND_VERIFICATION_CODE,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/public/sendotpcode',
        data: {
          consumerVerification: {
            otpType,
            verificationStatus: 'SendOTP',
            sendOTP: true
          },
          consumer
        }
      }
    }
  });
};

export const VALIDATE_VERIFICATION_CODE = 'REGISTER.VALIDATE_VERIFICATION_CODE';
export const VALIDATE_VERIFICATION_CODE_SUCCESS = 'REGISTER.VALIDATE_VERIFICATION_CODE_SUCCESS';
export const VALIDATE_VERIFICATION_CODE_FAIL = 'REGISTER.VALIDATE_VERIFICATION_CODE_FAIL';
export const validateVerificationCode = (otpType: 'email' | 'sms', pin: string) => (
  dispatch: Dispatch<AnyAction>,
  getState: GetState
) => {
  const consumer = registerDataAsConsumerSelector(getState());

  return dispatch({
    type: VALIDATE_VERIFICATION_CODE,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/public/validateotpcode',
        data: {
          consumerVerification: {
            otpType,
            verificationStatus: 'ValidateOTP',
            validateOTP: true,
            sendOTP: false,
            verifyUser: false,
            pin
          },
          consumer
        }
      }
    }
  });
};

export const VERIFY_PERSON = 'REGISTER.VERIFY_PERSON';
export const VERIFY_PERSON_SUCCESS = 'REGISTER.VERIFY_PERSON_SUCCESS';
export const VERIFY_PERSON_FAIL = 'REGISTER.VERIFY_PERSON_FAIL';
export const verifyPerson = () => (dispatch: Dispatch<AnyAction>, getState: GetState) => {
  const consumer = registerDataAsConsumerSelector(getState());

  return dispatch({
    type: VERIFY_PERSON,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/public/verify',
        data: {
          consumerVerification: {
            verificationStatus: 'VerifyUser',
            verifyUser: true,
            validateOTP: false,
            sendOTP: false
          },
          consumer
        }
      }
    }
  });
};

export const VERIFY_SOCIAL = 'REGISTER.VERIFY_SOCIAL';
export const VERIFY_SOCIAL_SUCCESS = 'REGISTER.VERIFY_SOCIAL_SUCCESS';
export const VERIFY_SOCIAL_FAIL = 'REGISTER.VERIFY_SOCIAL_FAIL';
export const verifySocialSecurityNumber = () => (
  dispatch: Dispatch<AnyAction>,
  getState: GetState
) => {
  const consumer = registerDataAsConsumerSelector(getState());

  return dispatch({
    type: VERIFY_SOCIAL,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/public/ssnverify',
        data: {
          consumerVerification: {
            verificationStatus: 'FallbackSSN',
            verifyUser: true,
            validateOTP: false,
            sendOTP: false
          },
          consumer
        }
      }
    }
  });
};

export const GET_REDIRECT_URL = 'REGISTER.GET_REDIRECT_URL';
export const GET_REDIRECT_URL_FAIL = 'REGISTER.GET_REDIRECT_URL_FAIL';
export const GET_REDIRECT_URL_SUCCESS = 'REGISTER.GET_REDIRECT_URL_SUCCESS';
export const getRedirectUrl = (actionType: UnifiedRedirectKey, redirectIdx: string): Action => ({
  type: GET_REDIRECT_URL,
  payload: {
    client: 'profile',
    request: {
      method: 'get',
      url: `/unified-redirect/${redirectIdx}/action/${actionType}`
    }
  }
});

export const resetRegistration = createAction('REGISTER.RESET_REGISTRATION');
